import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  ArrowRight, LifeBuoy, Heart, ShieldAlert, CheckCircle2,
  DollarSign, ArrowDown, TrendingUp, AlertTriangle, Target
} from "lucide-react";

type Step = "intro" | "setback" | "stabilize" | "triage" | "rebuild_cash" | "debt_plan" | "reentry" | "quiz" | "summary";
const STEPS: Step[] = ["intro", "setback", "stabilize", "triage", "rebuild_cash", "debt_plan", "reentry", "quiz", "summary"];

const RecoveryModeModule = () => {
  const navigate = useNavigate();
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const [step, setStep] = useState<Step>("intro");
  const [setbackType, setSetbackType] = useState<string | null>(null);
  const [cutAmount, setCutAmount] = useState(500);
  const [debtApproach, setDebtApproach] = useState<string | null>(null);
  const [reentryTiming, setReentryTiming] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const gate = moduleGates.find(g => g.moduleKey === "recovery_mode");
  const isLocked = !gate?.isUnlocked;
  if (loading) return null;

  return (
    <ModuleShell
      title="Recovery Mode"
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={gate?.missingPrereqs?.length ? `Complete: ${gate.missingPrereqs.join(", ")}` : "Complete Financial Recovery to unlock."}
      isCompleted={completed}
      completedMessage="You've practiced stabilizing, triaging, and rebuilding from financial setbacks."
      stepKey={step}
    >
      {step === "intro" && (
        <ModuleStepCard
          icon={<LifeBuoy className="w-8 h-8 text-primary" />}
          title="Recovery Mode"
          body="Financial setbacks happen to everyone — job loss, medical emergencies, market crashes, divorce. What matters is how you stabilize and rebuild. This module teaches survival-first financial thinking."
          highlights={["Stabilize before optimizing", "Cash preservation is priority #1", "Recovery is a process, not a moment"]}
          action={<Button onClick={() => setStep("setback")} className="w-full gap-2">Let's Begin <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "setback" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<AlertTriangle className="w-8 h-8 text-destructive" />} title="Your Setback Scenario" body="Choose the financial setback you're recovering from:" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "job_loss", label: "Job Loss", desc: "Primary income gone. You have 3 months of expenses saved. No immediate replacement." },
              { key: "medical", label: "Medical Emergency", desc: "$12,000 in unexpected medical bills. Insurance covered some, but not all." },
              { key: "market_crash", label: "Market Crash", desc: "Portfolio down 40%. No immediate income loss but net worth is devastated." },
            ].map(s => (
              <button key={s.key} className={`rounded-2xl border p-4 text-left transition-all ${setbackType === s.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setSetbackType(s.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{s.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("stabilize")} disabled={!setbackType} className="w-full gap-2">Continue <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "stabilize" && (
        <ModuleStepCard
          icon={<ShieldAlert className="w-8 h-8 text-accent" />}
          title="Step 1: Stabilize"
          body="Stop the bleeding. The first action in recovery is always to reduce outflows and preserve cash. Cancel non-essential subscriptions. Pause investments. Focus on covering food, shelter, and utilities."
          highlights={[
            "Cancel all non-essential spending immediately",
            "Pause retirement contributions temporarily",
            "Contact creditors about hardship programs",
            "Apply for any available assistance",
          ]}
          action={<Button onClick={() => setStep("triage")} className="w-full gap-2">Next: Triage Obligations <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "triage" && (
        <ModuleStepCard
          icon={<Target className="w-8 h-8 text-primary" />}
          title="Step 2: Triage Obligations"
          body="Not all bills are equal during a crisis. Prioritize in this order:"
          highlights={[
            "1. Housing (rent/mortgage) — keeps a roof over your head",
            "2. Food & utilities — basic survival",
            "3. Insurance — protects against further disaster",
            "4. Secured debt (car loan) — they can repossess",
            "5. Unsecured debt (credit cards) — negotiate, defer",
            "6. Everything else — pause or cancel",
          ]}
          action={<Button onClick={() => setStep("rebuild_cash")} className="w-full gap-2">Next: Rebuild Cash <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "rebuild_cash" && (
        <div className="space-y-6">
          <ModuleStepCard
            icon={<DollarSign className="w-8 h-8 text-primary" />}
            title="Step 3: Rebuild Cash"
            body="Cut expenses aggressively to rebuild your cash cushion. How much can you cut from monthly spending?"
            subtext={`Monthly cuts: $${cutAmount}`}
            action={<div />}
          />
          <Slider value={[cutAmount]} min={100} max={1500} step={50} onValueChange={([v]) => setCutAmount(v)} />
          <div className="flex justify-between text-xs text-muted-foreground"><span>$100</span><span>$1,500</span></div>
          <div className="rounded-2xl border border-border bg-card/50 p-4">
            <p className="text-xs text-muted-foreground">In 3 months, you'd save: <span className="text-primary font-mono">${(cutAmount * 3).toLocaleString()}</span></p>
            <p className="text-xs text-muted-foreground mt-1">In 6 months: <span className="text-primary font-mono">${(cutAmount * 6).toLocaleString()}</span></p>
          </div>
          <Button onClick={() => setStep("debt_plan")} className="w-full gap-2">Next: Debt Plan <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "debt_plan" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<ArrowDown className="w-8 h-8 text-accent" />} title="Step 4: Manage Debt During Recovery" body="If you're carrying debt during a crisis, choose your approach:" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "minimum", label: "Minimum Payments Only", desc: "Preserve cash. Pay minimums on everything. Focus on survival." },
              { key: "negotiate", label: "Negotiate with Creditors", desc: "Call each creditor. Request hardship programs, lower rates, or payment deferrals." },
              { key: "consolidate", label: "Consolidate Debt", desc: "If possible, combine debts into one lower-interest payment." },
            ].map(d => (
              <button key={d.key} className={`rounded-2xl border p-4 text-left transition-all ${debtApproach === d.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setDebtApproach(d.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{d.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{d.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("reentry")} disabled={!debtApproach} className="w-full gap-2">Next: Re-Entry <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "reentry" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<TrendingUp className="w-8 h-8 text-primary" />} title="Step 5: Risk Re-Entry" body="When should you start investing and taking financial risks again?" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "slow", label: "Slow & Steady", desc: "Rebuild emergency fund first (3-6 months). Resume investing only after you're stable." },
              { key: "moderate", label: "Moderate Re-Entry", desc: "Start small contributions immediately while rebuilding. Balance both." },
              { key: "aggressive", label: "Aggressive Comeback", desc: "Jump back in fast to make up lost time. Risky but could accelerate recovery." },
            ].map(r => (
              <button key={r.key} className={`rounded-2xl border p-4 text-left transition-all ${reentryTiming === r.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setReentryTiming(r.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{r.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{r.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("quiz")} disabled={!reentryTiming} className="w-full gap-2">Take Knowledge Check <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "quiz" && (
        <ModuleQuiz moduleKey="recovery_mode" onPass={() => { setCompleted(true); setStep("summary"); completeModule("recovery_mode"); }} />
      )}

      {step === "summary" && (
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground">Recovery Plan Complete</h2>
          <p className="text-sm text-muted-foreground">You stabilized, triaged, rebuilt cash, and planned re-entry. Recovery is a skill — and now you have it.</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full">Back to Dashboard</Button>
        </div>
      )}
    </ModuleShell>
  );
};

export default RecoveryModeModule;
